import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, Combobox } from "@similarweb/ui-components";

import { useElementSize } from "app/pages/sales-intelligence/hooks/useElementSize";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";
import { ScoringConfigType } from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

import { ScoringAddRuleButton } from "./ScoringAddRuleButton";

export type ScoringAddRuleTranslationKeys = {
    privateKey: string;
    sharedKey: string;
};

type ScoringAddRuleComboboxProps = {
    priority: LeadPriorityScore;
    canCreateShared: boolean;
    automationPrefix: string;
    addRuleTranslationKeys: ScoringAddRuleTranslationKeys;
    onAddRule: (scope: ScoringConfigType) => void;
};

export const ScoringAddRuleCombobox = ({
    priority,
    canCreateShared,
    automationPrefix,
    addRuleTranslationKeys,
    onAddRule,
}: ScoringAddRuleComboboxProps) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const { ref: triggerRef, size: triggerSize } = useElementSize();

    const addRuleLabel = `+ ${translate("si.pages.score_settings.board.column.add_rule")}`;

    if (!canCreateShared) {
        return (
            <ScoringAddRuleButton
                priority={priority}
                scope={ScoringConfigType.User}
                automationPrefix={automationPrefix}
                onAddRule={onAddRule}
            />
        );
    }

    const handleSelect = (scope: ScoringConfigType) => {
        setIsOpen(false);
        onAddRule(scope);
    };

    return (
        <Combobox
            placement="bottom-end"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            width={triggerSize?.width ? `${triggerSize.width}px` : undefined}
            data-automation={`${automationPrefix}.column.${priority}.add-rule`}
        >
            <Combobox.Trigger ref={triggerRef}>
                <Button
                    type="primary"
                    dataAutomation={`${automationPrefix}.column.${priority}.add-rule-trigger`}
                >
                    {addRuleLabel}
                </Button>
            </Combobox.Trigger>
            <Combobox.Content
                selectionMode="single"
                ariaLabel={translate("si.pages.score_settings.board.column.add_rule")}
            >
                <Combobox.Option
                    id={ScoringConfigType.User}
                    textValue={translate(addRuleTranslationKeys.privateKey)}
                    onClick={() => handleSelect(ScoringConfigType.User)}
                    data-automation={`${automationPrefix}.column.${priority}.add-private-rule`}
                >
                    <Combobox.OptionLabel>{translate(addRuleTranslationKeys.privateKey)}</Combobox.OptionLabel>
                </Combobox.Option>
                <Combobox.Option
                    id={ScoringConfigType.Account}
                    textValue={translate(addRuleTranslationKeys.sharedKey)}
                    onClick={() => handleSelect(ScoringConfigType.Account)}
                    data-automation={`${automationPrefix}.column.${priority}.add-shared-rule`}
                >
                    <Combobox.OptionLabel>{translate(addRuleTranslationKeys.sharedKey)}</Combobox.OptionLabel>
                </Combobox.Option>
            </Combobox.Content>
        </Combobox>
    );
};
